import RiAddLine from '@ben/icons/add-line';
import RiSubtractLine from '@ben/icons/subtract-line';
import IconButton from '@ben/orion/icon-button';
import Input from '@ben/orion/input';
import InputFeedback from '@ben/orion/input-feedback';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { EmployerFlagSlug } from '@ben/shared/hooks/use-employer-flag';
import useEmployerFlag from '@ben/shared/hooks/use-employer-flag/use-employer-flag';
import { lowerCase } from 'lodash';
import type { ChangeEvent } from 'react';
import { memo, useCallback, useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { Action } from './selection-button-group';
import SelectionButtonGroup from './selection-button-group';

type UnitType = 'DAYS' | 'HOURS';

/**
 * The two capacities driving the Buy/Sell toggle. Both come from the API,
 * are formula-aware and non-negative:
 * - `buyMax` is the field's effective `max` (how much can be bought),
 * - `sellMax` is the field's `sell_max` (how much can be sold).
 * Neither carries the sign convention of the stored value.
 */
interface IAdditionalConfig {
  buyMax?: number | string;
  sellMax?: number | string;
  interval?: number | string;
  unitType: UnitType;
}

export interface IBuyAndSellHoliday {
  /** Signed quantity: positive = bought, negative = sold, zero = none. */
  numberOfHolidays: string;
  setNumberOfHolidays: (props: string) => void;
  setHasMadeChanges?: (props: boolean) => void;
  disabled: boolean;
  additionalConfig: IAdditionalConfig;
  setValidation: (hasValidationError: boolean, message: string[]) => void;
  hideValidationMessage?: boolean;
}

const QUANTITY_LOWER_BOUND = 0;
const DEFAULT_INTERVAL = 0.5;

const toCapacity = (raw: number | string | undefined): number =>
  Math.abs(Number(raw));

const BuyAndSellHolidaySection = ({
  setNumberOfHolidays,
  numberOfHolidays,
  setHasMadeChanges,
  additionalConfig = { unitType: 'DAYS' },
  disabled,
  setValidation,
  hideValidationMessage = false,
}: IBuyAndSellHoliday) => {
  const { t } = useTranslation();

  const hideBuySellActionControls =
    useEmployerFlag(EmployerFlagSlug.ea_hide_buy_sell_action_controls) ?? false;

  const maxBuy = toCapacity(additionalConfig?.buyMax);
  const maxSell = toCapacity(additionalConfig?.sellMax);

  const isBothZero = maxSell === 0 && maxBuy === 0;
  const isBuyOnly = maxSell === 0 && !isBothZero;
  const isSellOnly = maxBuy === 0 && !isBothZero;

  const interval = Number(additionalConfig?.interval) || DEFAULT_INTERVAL;

  const signedValue = Number(numberOfHolidays);
  /** Unsigned quantity shown in the input. */
  const quantity = Math.abs(signedValue);

  /**
   * Which toggle to show when the value is exactly zero — there is no
   * sign to read, so this is the only genuinely ambiguous case and the
   * only place that needs to remember a user choice across renders.
   */
  const [zeroValueToggle, setZeroValueToggle] = useState<Action>(
    function deriveDefaultToggleForZeroValue() {
      return isSellOnly ? 'sell' : 'buy';
    }
  );

  /**
   * Buy vs sell. Derived fresh every render, not stored in state: a
   * non-zero value's sign is always authoritative, so a persisted sale
   * displays as a sale even if props (capacities, disabled) change later
   * without remounting the component — e.g. a background refetch after a
   * mutation (BEN-3687). Only a zero value is genuinely ambiguous and
   * falls back to the remembered toggle choice.
   */
  const selected: Action =
    signedValue < 0 ? 'sell' : signedValue > 0 ? 'buy' : zeroValueToggle;

  const maxForSelectedAction = selected === 'sell' ? maxSell : maxBuy;

  const sendValueToServer = useCallback(
    function sendSignedValueToServer(unsignedValue: string) {
      setNumberOfHolidays(
        selected === 'sell' ? `-${unsignedValue}` : unsignedValue
      );
      setHasMadeChanges && setHasMadeChanges(true);
    },
    [selected, setNumberOfHolidays, setHasMadeChanges]
  );

  const handleAdd: () => void = useCallback(
    function incrementQuantity() {
      if (disabled) return;

      const currentQuantity = Math.abs(Number(numberOfHolidays));
      if (currentQuantity + interval <= maxForSelectedAction) {
        sendValueToServer((currentQuantity + interval).toString());
      }
    },
    [
      disabled,
      numberOfHolidays,
      interval,
      maxForSelectedAction,
      sendValueToServer,
    ]
  );

  const handleMinus: () => void = useCallback(
    function decrementQuantity() {
      if (disabled) return;

      const currentQuantity = Math.abs(Number(numberOfHolidays));
      if (currentQuantity - interval >= QUANTITY_LOWER_BOUND) {
        sendValueToServer((currentQuantity - interval).toString());
      }
    },
    [disabled, numberOfHolidays, interval, sendValueToServer]
  );

  const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (newValue === '') {
      sendValueToServer('0');
      return;
    }

    const numericValue = Number.parseFloat(newValue);
    if (!Number.isNaN(numericValue)) {
      const clampedValue = Math.max(numericValue, QUANTITY_LOWER_BOUND);
      sendValueToServer(clampedValue.toString());
    }
  };

  const handleSelectionChange = (action: Action) => {
    setZeroValueToggle(action);
    // Switching action resets the quantity: a value entered for "buy"
    // must not silently become a sale (or vice versa). This also makes
    // the value zero, so `selected` immediately reflects `action` again
    // via the toggle above rather than a stale sign.
    setNumberOfHolidays('0');
    setHasMadeChanges && setHasMadeChanges(true);
  };

  // t(`words.buy`)
  // t(`words.sell`)
  const selectedOption = lowerCase(t(`words.${selected}`));
  // t(`words.days`)
  // t(`words.hours`)
  const selectedUnit = t(`words.${lowerCase(additionalConfig.unitType)}`);

  const hasError = quantity > maxForSelectedAction;
  const shouldShowValidation = hasError && !disabled;

  // Guard from BEN-3556: while *editing* a buy-only field, two stale
  // states must not survive into a submission. Must never run when
  // read-only (`disabled`) — there the stored value has to be displayed
  // truthfully (BEN-3687).
  useEffect(
    function normalizeStaleSaleOnBuyOnlyField() {
      if (disabled || !isBuyOnly) return;

      // 1. the zero-value toggle still pointing at the now-unavailable
      //    Sell tile (capacities changed while the value was zero);
      if (zeroValueToggle === 'sell') {
        setZeroValueToggle('buy');
      }

      // 2. a persisted negative value (a stale sale from an earlier
      //    config) that would be re-submitted as-is and rejected by the
      //    API, so flip it to a buy.
      if (signedValue < 0) {
        setNumberOfHolidays(Math.abs(signedValue).toString());
        setHasMadeChanges?.(true);
      }
    },
    [
      disabled,
      isBuyOnly,
      zeroValueToggle,
      signedValue,
      setNumberOfHolidays,
      setHasMadeChanges,
    ]
  );

  // The host hands down a new callback identity every render, so it is read
  // as an event rather than as an effect dependency.
  const reportValidation = useEffectEvent(
    (hasValidationError: boolean, message: string[]) =>
      setValidation?.(hasValidationError, message)
  );
  useEffect(
    function reportValidationToParent() {
      reportValidation(shouldShowValidation, [
        t('benefit.maxHoliday', { type: selectedOption }),
      ]);
    },
    [shouldShowValidation, t, selectedOption]
  );

  return (
    <Container>
      <SelectionButtonGroup
        value={selected}
        onChange={handleSelectionChange}
        name="buy-sell-selection"
        disabled={disabled}
        isBuyOnly={isBuyOnly}
        isSellOnly={isSellOnly}
      />
      <ActionErrorWrapper>
        <ActionButtonContainer>
          <ActionInformationContainer $disabled={disabled}>
            <Typography variant={TypographyVariant.bodyLg3}>
              {t('benefit.buySellHolidayByUnit', {
                unit: selectedUnit,
                type: selectedOption,
              })}
            </Typography>
            {!disabled && (
              <Typography variant={TypographyVariant.bodySm2}>
                {t('benefit.max', { num: maxForSelectedAction })}
              </Typography>
            )}
          </ActionInformationContainer>
          <ActionContainer>
            {!hideBuySellActionControls && (
              <IconButton
                size="lg"
                icon={RiSubtractLine}
                disabled={disabled || quantity <= QUANTITY_LOWER_BOUND}
                onClick={handleMinus}
                data-testid="Subtract-icon-button"
              />
            )}
            <Input
              value={quantity || ''}
              onChange={handleValueChange}
              name="number-of-holidays"
              width="60px"
              showLabel={false}
              type="number"
              disabled={disabled}
              step={interval}
              min={QUANTITY_LOWER_BOUND}
              max={maxForSelectedAction}
            />
            {!hideBuySellActionControls && (
              <IconButton
                data-testid="Add-icon-button"
                size="lg"
                icon={RiAddLine}
                disabled={disabled || quantity >= maxForSelectedAction}
                onClick={handleAdd}
              />
            )}
          </ActionContainer>
        </ActionButtonContainer>
        {shouldShowValidation && !hideValidationMessage && (
          <InputFeedback
            errorMessage={t('benefit.maxHoliday', { type: selectedOption })}
          />
        )}
      </ActionErrorWrapper>
    </Container>
  );
};

export default memo(BuyAndSellHolidaySection);

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `}
`;

const ActionButtonContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;

const ActionErrorWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const ActionInformationContainer = styled.div<{ $disabled: boolean }>`
  ${({ theme, $disabled }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    opacity: ${$disabled ? 0.4 : 1};
  `}
`;

const ActionContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;
